// The badge a fan is closest to earning, with what is left to do. Built from
// `/engagement/me`: the catalogue, the badges held, the missions in progress,
// the streaks and the fan points. Pure; safe on partial payloads.
import { FAN_LEVELS, levelProgress, streakList } from "./levels";

const STREAK_DAYS = [7, 30, 100];

const keyOf = (b) => String(b?.key || b?.badgeKey || "");

function heldKeys(badges) {
  return new Set((Array.isArray(badges) ? badges : []).map(keyOf).filter(Boolean));
}

function catalogEntry(catalog, key) {
  return (Array.isArray(catalog) ? catalog : []).find((b) => keyOf(b) === key) || null;
}

/**
 * @returns {{ key, kind, title, description, pct, remainingText } | null}
 * pct is 0-100 progress toward the badge; remainingText is one short line of
 * what is still needed. Ties on pct break toward the smaller remaining count.
 */
export function nextBadge(me) {
  if (!me) return null;
  const held = heldKeys(me.badges);
  const catalog = Array.isArray(me.badgeCatalog) ? me.badgeCatalog : [];
  const candidates = [];

  // Fan level badge.
  const points = Number(me.fanPoints) || 0;
  const lp = levelProgress(points, FAN_LEVELS);
  if (lp.next) {
    const key = `level.${lp.next.name.toLowerCase()}`;
    if (!held.has(key)) {
      const entry = catalogEntry(catalog, key);
      candidates.push({
        key,
        kind: "level",
        title: entry?.title || lp.next.name,
        description: entry?.description || `Reach fan level ${lp.next.name}`,
        pct: lp.pct,
        remaining: lp.pointsToNext,
        remainingText: `${Math.ceil(lp.pointsToNext).toLocaleString()} more points`,
      });
    }
  }

  // Daily listening streak badges.
  const daily = streakList(me.streaks).find((s) => s.key === "dailyListen");
  const current = Number(daily?.current) || 0;
  for (const days of STREAK_DAYS) {
    const key = `streak.dailyListen.${days}`;
    if (held.has(key) || current >= days) continue;
    const entry = catalogEntry(catalog, key);
    const left = days - current;
    candidates.push({
      key,
      kind: "streak",
      title: entry?.title || `${days}-day listener`,
      description: entry?.description || `Complete a song every day for ${days} days`,
      pct: Math.round((current / days) * 100),
      remaining: left,
      remainingText: `${left} more ${left === 1 ? "day" : "days"} in a row`,
    });
    break; // only the nearest streak tier is a candidate
  }

  // Once-only missions still open.
  const missions = Array.isArray(me.missions) ? me.missions : [];
  for (const m of missions) {
    if ((m.period || "once") !== "once" || m.completedAt) continue;
    const key = `mission.${m.key}`;
    if (held.has(key)) continue;
    const target = Math.max(1, Number(m.target) || 1);
    const progress = Math.min(target, Number(m.progress) || 0);
    const left = target - progress;
    candidates.push({
      key,
      kind: "mission",
      title: m.title || m.key,
      description: m.description || "",
      pct: Math.round((progress / target) * 100),
      remaining: left,
      remainingText: target === 1 ? m.description || "One more step" : `${left} of ${target} to go`,
    });
  }

  if (candidates.length === 0) return null;
  candidates.sort((a, b) => b.pct - a.pct || a.remaining - b.remaining);
  const best = candidates[0];
  return { key: best.key, kind: best.kind, title: best.title, description: best.description, pct: best.pct, remainingText: best.remainingText };
}

export default nextBadge;
